import React, {
  FC, memo, useCallback, useMemo, useState,
} from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { AlarmCallType } from '@x-guard/xgac-types/xgac';

import { PhoneCall as PhoneCallType } from 'types';
import { useLocale } from 'lib';
import { phoneCallParticipantsSelector, setPhoneCallMuted } from 'features/details/phoneCalls';
import { alarmCreatorSelector } from 'features/details';
import { resultSelector as helpersSelector } from 'features/details/helpers';
import { userContextSelector } from 'features/identity/auth';
import { DisconnectedView } from '../disconnected';
import { ConnectedView } from '../connected';
import Grid from '../participants/grid';
import Item from '../participants/item';
import styles from '../style.module.scss';
import participantsStyles from '../participants/style.module.scss';

type PhoneCallProps = {
  phoneCall: PhoneCallType;
  /** Off where everyone in the call is already listed elsewhere (the helpers block). */
  showParticipants?: boolean;
  /** Another call needs attention first: hide the join code behind a warning until asked for (not once you are connected). */
  listenFirst?: boolean;
};

/**
 * A main api call (alarm call or helper call) an operator can join by phone: shows the join code
 * until the operator is connected, then the microphone toggle, plus everyone who is in the call.
 */
const PhoneCall: FC<PhoneCallProps> = ({
  phoneCall: { call, code, muted }, showParticipants = true, listenFirst = false,
}) => {

  const { t } = useLocale();
  const dispatch = useDispatch();
  const [codeRevealed, setCodeRevealed] = useState(false);
  const participants = useSelector(useMemo(() => phoneCallParticipantsSelector(call._id), [call._id]));
  const helpers = useSelector(helpersSelector);
  const alarmCreator = useSelector(alarmCreatorSelector);
  const userName = useSelector(userContextSelector)?.user?.name;
  // You first.
  const inCall = participants.filter(({ inCall: connected }) => connected).sort((a, b) => Number(b.isSelf) - Number(a.isSelf));
  const self = inCall.find(({ isSelf }) => isSelf);
  const title = call.type === AlarmCallType.AlarmCall
    ? t('Alarm_Detail_DCC.Phone_Call.Alarm_Call')
    : t(self ? 'Alarm_Detail_DCC.Phone_Call.Helper_Call_Connected' : 'Alarm_Detail_DCC.Phone_Call.Helper_Call');

  const toggleMuted = useCallback(() => {

    dispatch(setPhoneCallMuted({
      alarmCallId: call._id,
      muted: !(self?.participant.state.muted ?? muted),
      participantId: self?.participant._id,
    }));

  }, [dispatch, call._id, self, muted]);

  if (listenFirst && !self && !codeRevealed) {

    return (
      <div className={styles.listenFirst}>
        <span>{t('Alarm_Detail_DCC.Phone_Call.Listen_First')}</span>
        <button type="button" onClick={() => setCodeRevealed(true)}>{t('Alarm_Detail_DCC.Phone_Call.Show_Helper_Code')}</button>
      </div>
    );

  }

  return (
    <div className={styles.created}>
      {self ? (
        <ConnectedView title={title} muted={self.participant.state.muted} onToggleMuted={toggleMuted}/>
      ) : (
        <DisconnectedView title={title} code={code} muted={muted} onChangeMute={toggleMuted}/>
      )}
      {showParticipants && inCall.length > 0 && (
        <div className={participantsStyles.body}>
          <Grid title={t('Alarm_Detail_DCC.Connected.Connected_To')}>
            {inCall.map(({ participant, isSelf }) => (
              <Item
                key={participant._id}
                image={(participant.ref?._id === alarmCreator?._id ? alarmCreator?.properties?.image : undefined)
                  ?? helpers.find(({ asset }) => asset?._id && asset._id === participant.ref?._id)?.asset?.properties?.image}
                personId={participant.ref?._id}
                name={(isSelf && userName) || participant.name || (participant.ref?._ref === 'User' ? t('Alarm_Detail_DCC.Phone_Call.Operator') : '')}
                label={isSelf ? t('Alarm_Detail_DCC.Connected.Self') : undefined}
                self={isSelf}
                timer={{ initialTimeMs: moment.utc().valueOf() - moment.utc(participant.state.connectedAt).valueOf() }}
                muted={participant.state.muted}
                toggleMuted={isSelf ? toggleMuted : () => undefined}
              />
            ))}
          </Grid>
        </div>
      )}
    </div>
  );

};

export default memo(PhoneCall);
